import React, { useEffect, useState } from "react";
import { useMutation } from '@apollo/client';
import AuthLayout from "../../layouts/AuthLayout";
import {TextField, Button} from "../../components";
import { REQUEST_PASSWORD_RESET, RESET_PASSWORD } from './graphql';

const SPECIAL_CHARACTER_REGEX = /[!@£#$%^&*()_+=[\]{}|\\:;"'/?.,<>`~-]/;

export default function PasswordReser() {
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [token, setToken] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [requestPasswordResetMutation] = useMutation(REQUEST_PASSWORD_RESET, {
        onCompleted: ({ requestPasswordReset }) => {
            setLoading(false);

            if (requestPasswordReset?.__typename === 'MutationRequestPasswordResetSuccess') {
                setMessage('Please check your email for the reset instructions.');
            } else {
                setErrorMessage(requestPasswordReset?.message ?? 'Unable to send reset instructions.');
            }
        },
        onError: () => {
            setLoading(false);
            setErrorMessage('Unable to send reset instructions.');
        }
    });

    const [resetPasswordMutation] = useMutation(RESET_PASSWORD, {
        onCompleted: ({ resetPassword }) => {
            setLoading(false);

            if (resetPassword?.__typename === 'MutationResetPasswordSuccess') {
                window.location.href = '/login';
            } else {
                setErrorMessage(resetPassword?.message ?? 'Unable to reset password.');
            }
        },
        onError: () => {
            setLoading(false);
            setErrorMessage('Unable to reset password.');
        }
    });

    const onSubmit = (event) => {
        setLoading(true);
        setErrorMessage('');

        event.preventDefault();
      
        if (token) {
            changePassword();
        } else {
            requestPasswordReset();
        }

       

    }

    const label = token ? "Change my password" : "Send me password reset link";

    const changePassword = () => {
        resetPasswordMutation({
            variables: {
                input: {
                    token,
                    email: email.trim().toLowerCase(),
                    password,
                    confirm: confirmPassword
                }
            }
        });
    }

    const requestPasswordReset = () => {
        requestPasswordResetMutation({
            variables: {
                input: {
                    email: email.trim().toLowerCase()
                }
            }
        });
    }

    const passwordRequirements = [
        {
            label: 'At least 6 characters',
            met: password.length >= 6
        },
        {
            label: 'At least 1 uppercase letter',
            met: /[A-Z]/.test(password)
        },
        {
            label: 'At least 1 lowercase letter',
            met: /[a-z]/.test(password)
        },
        {
            label: 'At least 1 number',
            met: /[0-9]/.test(password)
        },
        {
            label: 'At least 1 special character, such as !, @, # or ?',
            met: SPECIAL_CHARACTER_REGEX.test(password)
        }
    ];

    useEffect(() => {
        const token = getToken();

        if (token) {
            setToken(token)
        }
    }, []);

    const formMarkup = (
        <form className="space-y-6" onSubmit={onSubmit}>
                <TextField
                    isRequired
                    value={email}
                    label="Email address" 
                    name="email" 
                    type="email"
                    autoComplete="username"
                    onChange={setEmail}
                />

                {token && (
                    <> 
                        <TextField
                            isRequired
                            value={password}
                            label="New password" 
                            name="password" 
                            type="password"
                            autoComplete="new-password"
                            onChange={setPassword}
                        />

                        <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                            <p className="mb-3 text-sm font-medium text-gray-700">Your new password must include:</p>
                            <ul className="space-y-2">
                                {passwordRequirements.map((requirement) => (
                                    <li key={requirement.label} className="flex items-center text-sm text-gray-700">
                                        <span className={`mr-3 h-2.5 w-2.5 rounded-full ${requirement.met ? 'bg-green-500' : 'bg-gray-300'}`} />
                                        <span>{requirement.label}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <TextField
                            isRequired
                            value={confirmPassword}
                            label="Confirm password" 
                            name="password" 
                            type="password"
                            autoComplete="new-password"
                            onChange={setConfirmPassword}
                        />
                    </>
                )}

                
                <div>
                    <Button loading={ loading } submittable label={ label } />
                </div>
            </form>
    );

    const messageMarkup = (
        <div className="flex items">
            <p className="p-6 bg-green-500 text-white rounded-md">{ message } </p>
        </div>
    )

    const errorMarkup = errorMessage ? (
        <div className="flex items mb-4">
            <p className="p-4 bg-red-500 text-white rounded-md">{ errorMessage }</p>
        </div>
    ) : null

    return (
        <AuthLayout heading="Password Reset">
           { errorMarkup }
           { message ? messageMarkup : formMarkup }
        </AuthLayout>
    );
}

function getToken() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const token = urlParams.get('token') ?? '';
    return token.match(/[a-f0-9]{32}/i)?.[0] ?? '';
}
